import {
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  Button,
  Box,
  Flex,
  InputLeftAddon,
  InputRightAddon,
  InputGroup,
  Select,
  Checkbox,
  Stack,
  FormHelperText,
  Link,
  Text,
  HStack
} from '@chakra-ui/react'
import hotkeys from 'src/lib/hotkeys'
import { useHotkeys } from 'react-hotkeys-hook'
import { useMutation, useQuery } from '@apollo/client'
import { gql } from '@apollo/client'
import { useForm, Controller } from 'react-hook-form'
import { motion } from 'framer-motion'
import { useRef, useEffect } from 'react'
import { CheckIcon } from '@chakra-ui/icons'

const MotionButton = motion(Button)

const UPDATE_SCHOOL = gql`
  mutation UpdateSchoolMutation($id: Int!, $input: UpdateSchoolInput!) {
    updateSchool(id: $id, input: $input) {
      id
      name: schoolName
      subdomain
      usedForTesting
      dormant
      useNewFront
    }
  }
`

const SCHOOL_PACKAGES_QUERY = gql`
  query SchoolPackagesQuery($schoolId: Int!) {
    schoolPackages(schoolId: $schoolId) {
      id
      name
      status
    }
  }
`

const UPDATE_SCHOOL_PACKAGE = gql`
  mutation UpdateSchoolPackageMutation(
    $name: String!
    $schoolId: Int!
    $input: UpdateSchoolPackageInput!
  ) {
    updateSchoolPackage(schoolId: $schoolId, name: $name, input: $input) {
      id
      status
    }
  }
`

const SchoolForm = ({ school, onSave = () => { } }) => {
  const { handleSubmit, register, control, formState: { errors }, getValues, setFocus } = useForm({
    defaultValues: {
      schoolName: school.name || '',
      acronym: school.acronym || '',
      subdomain: school.subdomain || '',
      edu_domain: school.edu_domain || '',
      usedForTesting: !!school.usedForTesting,
      dormant: !!school.dormant,
      useNewFront: !!school.useNewFront,
      googlePlaceId: school.googlePlaceId || '',
      package: school.package || 'legacy',
    },
  })
  // const inputRef = useRef()
  const [update, { loading, error }] = useMutation(UPDATE_SCHOOL, {
    refetchQueries: ['SchoolDashQuery'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      onSave()
    },
  })
  const onSubmit = (params) => {
    const input = {
      ...params,
      usedForTesting: !!params.usedForTesting,
      dormant: !!params.dormant,
      useNewFront: !!params.useNewFront,
    }
    update({ variables: { id: school.id, input } })
  }

  const Packages = [
    'core',
    'website', 'website_premier',
    'communication', 'communication_premier',
    'learning', 'learning_premier'
  ]
  const { data: schoolPackagesData } = useQuery(SCHOOL_PACKAGES_QUERY, {
    variables: { schoolId: school.id },
  })
  const [updatePackage] = useMutation(UPDATE_SCHOOL_PACKAGE, {
    refetchQueries: [
      { query: SCHOOL_PACKAGES_QUERY, variables: { schoolId: school.id } },
    ],
    awaitRefetchQueries: true,
  })

  const allMappedPackages = Packages.map(name => {
    const existing = schoolPackagesData?.schoolPackages?.find(pkg => pkg.name === name);
    return existing || { name: name, status: 0 };
  });

  const handleUpdatePackage = async (pkgName, status) => {
    await updatePackage({
      variables: {
        name: pkgName,
        schoolId: school.id,
        input: { status: status },
      },
    })
  }

  useEffect(() => {
    setFocus("schoolName")
  }, [setFocus])

  useHotkeys(
    hotkeys.saveEdit,
    (e) => {
      e.preventDefault()
      onSubmit(getValues())
    },
    { filterPreventDefault: true }
  )

  return (
    <Flex padding={30} justifyContent="center">
      <Box maxW={663}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {error && <div style={{ color: 'red' }}>{error.message}</div>}
          <Stack spacing={30}>
            <FormControl isInvalid={errors.schoolName}>
              <Input
                size="lg"
                defaultValue={school.name}
                placeholder="School Name"
                {...register("schoolName", { required: true })}
              />
              <FormErrorMessage>School name is required</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.acronym}>
              <FormLabel htmlFor="acronym">Acronym</FormLabel>
              <Input
                size="lg"
                defaultValue={school.acronym}
                placeholder="Acronym - ex. SQS"
                {...register("acronym")}
              />
            </FormControl>
            <FormControl isInvalid={errors.subdomain}>
              <FormLabel htmlFor="subdomain">Subdomain</FormLabel>
              <InputGroup>
                <InputLeftAddon color="gray.500">https://</InputLeftAddon>
                <Input
                  defaultValue={school.subdomain}
                  placeholder="Subdomain"
                  {...register("subdomain", {
                    required: true,
                    pattern: /^[a-z0-9\-]+$/,
                  })}
                />
                <InputRightAddon color="gray.500">
                  beehively.com
                </InputRightAddon>
              </InputGroup>
              <FormErrorMessage>Valid webaddress is required</FormErrorMessage>
              {!errors.subdomain && (
                <FormHelperText id="schoolName-helper-text">
                  Change school webaddress. If changed incorrectly people at
                  this school won&apos;t be able to reach their beehively portal
                </FormHelperText>
              )}
            </FormControl>
            <FormControl isInvalid={errors.edu_domain}>
              <FormLabel htmlFor="edu_domain">Gsuite Domain</FormLabel>
              <Input
                defaultValue={school.edu_domain}
                placeholder="Gsuite Domain"
                {...register("edu_domain", {
                  pattern: /(?=^.{4,253}$)(^((?!-)[a-zA-Z0-9-]{0,62}[a-zA-Z0-9]\.)+[a-zA-Z]{2,63}$)/,
                })}
              />
              <FormErrorMessage>Gsuite Domain seems invalid</FormErrorMessage>
              <FormHelperText id="edu_domain-helper-text">
                This is school&apos;s gsuite domain, used for many integrations
                like google classroom or drive integrations. Please be careful
                when changing this.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <Checkbox
                defaultIsChecked={school.usedForTesting}
                {...register("usedForTesting")}
              >
                Testing school?
              </Checkbox>
              <FormHelperText id="usedForTesting-helper-text">
                Mark school as testing school, useful for filtering schools in
                this dashboard.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <Checkbox
                defaultIsChecked={school.dormant}
                {...register("dormant")}
              >
                Mark school dormant
              </Checkbox>
              <FormHelperText id="dormant-helper-text">
                Useful for filtering schools in this dashboard.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <Controller
                name="useNewFront"
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <Checkbox
                    ref={ref}
                    isChecked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  >
                    Enable New Front
                  </Checkbox>
                )}
              />
              <FormHelperText id="useNewFront-helper-text">
                When enabled, people can use this school on New Front (3.).
                When disabled, logging in from New Front redirects to the B2
                home page for this school.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="googlePlaceId">
                Google place ID{' '}
                {school.googlePlaceUrl ? (
                  <Link
                    href={school.googlePlaceUrl}
                    color="blue.500"
                    isExternal
                  >
                    view in map
                  </Link>
                ) : null}
              </FormLabel>
              <Input
                defaultValue={school.googlePlaceId}
                placeholder="Google place ID"
                {...register("googlePlaceId")}
              />
              <FormHelperText id="googlePlaceId-helper-text">
                If you are marking school dormant, you can make this field empty
                so correct school can sign up for this google place ID.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="package">School package</FormLabel>
              <Select
                defaultValue={school.package}
                {...register("package")}
              >
                <option value="legacy">Legacy</option>
                <option value="regular">
                  Regular (Unlimited w/o custom fields)
                </option>
                <option value="unlimited">Unlimited</option>
                <option value="reservations_unlimited">
                  Unlimited with reservations
                </option>
                <option value="free">Free School</option>
                <option value="single_classroom">
                  Single Classroom(deprecated)
                </option>
                <option value="distance_learning">Distance Learning</option>
                <option value="new_school">New School</option>
                <option value="enterprise">Enterprise</option>
                <option value="cms_only">CMS Only</option>
              </Select>
              <FormErrorMessage name="package" />
              <FormHelperText id="package-helper-text">
                If you are unsure what is this, do not change from{' '}
                {school.package} to anything else. If changed incorrectly, it
                will change how school experiance beehively portal
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>New Front School Packages</FormLabel>
              <FormHelperText id="package-helper-text" paddingBottom={2}>
                <b>Important Notice:</b>
                <FormHelperText id="new-school-package-helper-text">
                  The following packages are intended exclusively for schools registered through the New Front platform. Please do not apply these packages to existing live schools, as doing so may disrupt access to current features. Migration of existing schools to the New Front platform is not yet supported.
                </FormHelperText>
              </FormHelperText>

              <Stack spacing={2}>
                {allMappedPackages?.map((pkg) => (
                  <Flex
                    key={pkg.name}
                    align="center"
                    justify="space-between"
                    p={2}
                    borderWidth="1px"
                    borderRadius="md"
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      {pkg.name} {pkg.status === 1 ? <CheckIcon style={{ color: 'green' }} /> : ''}
                    </Text>
                    <HStack spacing={1}>
                      {pkg.name === 'core' &&
                        <Button
                          size="xs"
                          colorScheme="gray"
                          onClick={() => (pkg.status === 0) ? handleUpdatePackage(pkg.name, 1) : ''}
                          disabled={pkg.status === 1}
                        >
                          Default
                        </Button>
                      }
                      {pkg.name !== 'core' && (pkg.status === 0 || pkg.status === 2) && (
                        <Button
                          size="xs"
                          colorScheme={pkg.id ? 'green' : 'yellow'}
                          onClick={() => (pkg.status === 0 || pkg.status === 2) ?
                            handleUpdatePackage(pkg.name, 1) :
                            ''
                          }
                          disabled={pkg.status === 1}
                        >
                          {pkg.id ? 'Approve' : 'Assign'}
                        </Button>
                      )}
                      {pkg.status === 2 &&
                        <Button size="xs" colorScheme="red" disabled={true}>
                          Cancelled
                        </Button>
                      }
                      {pkg.name !== 'core' && pkg.status === 1 &&
                        <Button size="xs" colorScheme="red" onClick={() => handleUpdatePackage(pkg.name, 2)}>
                          Downgrade
                        </Button>
                      }
                    </HStack>
                  </Flex>
                ))}
              </Stack>
            </FormControl>

            <MotionButton
              colorScheme="teal"
              isLoading={loading}
              loadingText="Please wait"
              whiletap={{ scale: 0.9 }}
              type="submit"
            >
              Save
            </MotionButton>
          </Stack>
        </form>
      </Box>
    </Flex>
  )
}

export default SchoolForm
